// ─────────────────────────────────────────────
//  TimelineStatusBand — four at-a-glance status strips.
//
//  EMERGENCIES · BREAKS WAITING · RELIEF IN PROGRESS
//
//  URGENCIES was here and was removed on request 2026-08-13. It was reading 8
//  with no timestamps on any of them, which said more about unresolved alerts
//  sitting on the bus than about the department. The count itself was real; the
//  strip just was not earning its place.
//
//  Each is a count, a plain-English message, and a freshness stamp. They sit
//  ABOVE the working cards because they are the "is anything wrong right now"
//  layer; the cards below are the "what do I do about it" layer.
//
//  ⚠️ EVERY COUNT HERE IS REAL. None of these strips has a mock, a placeholder,
//  or a default. Where the data cannot be trusted — the alert bus covers a
//  different campus — the strip REFUSES rather than showing a zero, because a
//  calm "0 EMERGENCIES" on a board that is not receiving that campus's alerts is
//  the most dangerous thing this page could print.
//
//  Purely presentational: every value arrives as a prop.
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../../theme/tokens";

const { color, font, radius } = tokens;

// " · oldest 3 d" — the qualifier that stops a stale queue reading as a crisis.
//
// ⚠️ AN ALERT NEVER EXPIRES IN THIS APP. `listenToAlerts` filters on facility and
// status only, with no date bound, and `isLiveAlert` is purely status-based — so
// anything nobody explicitly RESOLVED stays live forever and keeps arriving on
// every board. Age is therefore not decoration: without it, eight alerts nobody
// closed last month are indistinguishable from eight rooms in trouble right now.
function age(min) {
  // ⚠️ NULL IS SAID OUT LOUD, NOT OMITTED. `alertSentAt` returns null when an
  // alert carries no `sentAt` — which is the real state of the alerts currently
  // on this facility's bus. Leaving the clause off would render "8 rooms asking
  // for help." with no qualifier, and a bare count READS AS RECENT. An alert of
  // unknown age is exactly the one you cannot assume is recent.
  if (min == null) return " · age unknown";
  if (min < 60) return ` · oldest ${min} min`;
  if (min < 60 * 24) return ` · oldest ${Math.round(min / 60)} h`;
  return ` · oldest ${Math.round(min / (60 * 24))} d`;
}

// Tone is driven by the COUNT, not by the section. A section is not important
// because of what it is called; it is important when it has something in it.
// Zero is calm everywhere, which keeps the saturated ink for real events.
const TONE = {
  alarm: { ink: "#991b1b", bg: "#fef2f2", line: color.accent.error },
  warn: { ink: "#7a4f00", bg: "#fef8ec", line: color.accent.closing },
  info: { ink: "#1e3a8a", bg: "#e8edf9", line: color.accent.primary },
  calm: { ink: color.text.secondary, bg: color.surface.card, line: color.border.default },
};

function Strip({ title, count, message, tone, unavailable, stamp, children }) {
  const t = unavailable ? TONE.calm : TONE[count > 0 ? tone : "calm"] || TONE.calm;
  return (
    <section
      style={{
        ...s.strip,
        background: unavailable ? undefined : t.bg,
        borderLeftColor: t.line,
        ...(unavailable ? s.stripUnavailable : {}),
      }}
      data-status={title}
      data-count={unavailable ? "unavailable" : count}
    >
      <header style={s.head}>
        <h3 style={{ ...s.title, color: t.ink }}>
          {title}
          {/* The count travels WITH the title, as one phrase. A number alone in
              a coloured box is unreadable at a glance and invisible in
              greyscale. */}
          <span style={{ ...s.count, color: t.ink }}>{unavailable ? "—" : count}</span>
        </h3>
        {stamp && <span style={s.stamp}>{stamp}</span>}
      </header>
      <p style={{ ...s.message, color: unavailable ? color.text.muted : t.ink }}>{message}</p>
      {children}
    </section>
  );
}

export default function TimelineStatusBand({
  emergencies = 0,
  breaksWaiting = 0,
  reliefInProgress = 0,
  reliefRows = [],
  oldestBreakWaitMin = null,
  breakWaitingRows = [],
  oldestEmergencyMin = null,
  emergencyRows = [],
  alertsCovered = true,
  stamp,
  // Sounding the all-clear. The row decides whether the control renders
  // (`row.canClear`, computed where the raw alert lives); this component only
  // draws it and calls back with the alert id.
  onClearEmergency = null,
  // Confirming a staged handover: the incoming provider BECOMES the room's
  // provider for the rest of the day. Absent (not disabled) when the caller
  // passes nothing, so a read-only board renders no control at all.
  onConfirmRelief = null,
  busy = false,
}) {
  // Alert-derived strips cannot be drawn for a facility the bus does not cover.
  // Relief comes from the assignments stream, which IS scoped to the facility on
  // screen, so it stays live and keeps reporting.
  const na = !alertsCovered;
  return (
    <div style={s.band}>
      <Strip
        title="EMERGENCIES"
        count={emergencies}
        tone="alarm"
        unavailable={na}
        stamp={stamp}
        message={
          na
            ? "Not available for this facility — alerts follow your signed-in campus."
            : emergencies === 0
              ? "No live emergencies."
              : `${emergencies} live ${emergencies === 1 ? "emergency" : "emergencies"}${age(oldestEmergencyMin)}.`
        }
      >
        {/* WHO HAS PICKED IT UP, named. Acknowledgement in this app is
            PER-USER — it clears the alert from the acknowledger's own screen
            and leaves the shared board untouched (alerts.js
            `unacknowledgedEmergenciesForUser`). So on a wall display "1 live
            emergency" cannot tell you whether a doctor is already on the way
            or whether the room is still shouting into an empty corridor, and
            those call for opposite actions.
            ⚠️ NOT-YET-ACKNOWLEDGED IS THE LOUD STATE, not the quiet one. */}
        {!na && emergencyRows.length > 0 && (
          <ul style={s.handoffs}>
            {emergencyRows.map((r, i) => (
              <li key={r.id || `${r.roomName}-${i}`} style={s.handoff}>
                {r.roomName && <span style={s.hoRoom}>{r.roomName}</span>}
                {r.ackNames?.length > 0 ? (
                  <>
                    <span style={s.ackLabel}>acknowledged by</span>
                    <span style={s.hoIn}>{r.ackNames.join(", ")}</span>
                    {/* An acknowledger this board cannot name still counts. */}
                    {r.ackUnnamed > 0 && (
                      <span style={s.ackLabel}>
                        +{r.ackUnnamed} not named
                      </span>
                    )}
                  </>
                ) : r.ackCount > 0 ? (
                  <span style={s.hoIn}>
                    acknowledged · {r.ackCount === 1 ? "name unknown" : `${r.ackCount} people, names unknown`}
                  </span>
                ) : (
                  <span style={s.hoUnack}>NOBODY HAS ACKNOWLEDGED</span>
                )}
                {/* CLEAR = THE ALL-CLEAR, and it is deliberately the only
                    control on this strip. It renders ONLY when this viewer may
                    sound it for THIS alert — the room's own supervising doctor,
                    the AIC of a floor-wide alert, or an admin. Showing it to
                    everyone and letting the server refuse would invite the one
                    person who cannot clear an emergency to keep pressing it
                    during one.

                    ⚠️ NO CONFIRM STEP, ON PURPOSE. This is pressed during an
                    emergency by the doctor who just handled it; a modal asking
                    "are you sure" costs seconds at the worst possible moment and
                    is the kind of friction people learn to click through blind.
                    Clearing is also recoverable — the room can raise another in
                    one tap — whereas a delayed all-clear is not. */}
                {r.canClear && onClearEmergency && r.id && (
                  <button
                    type="button"
                    style={s.clearBtn}
                    onClick={() => onClearEmergency(r.id)}
                    aria-label={`Clear the emergency${r.roomName ? ` in ${r.roomName}` : ""}`}
                  >
                    Clear
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Strip>
      <Strip
        title="BREAKS WAITING"
        count={breaksWaiting}
        tone="warn"
        unavailable={na}
        stamp={stamp}
        message={
          na
            ? "Not available for this facility."
            : breaksWaiting === 0
              ? "Nobody is waiting for a break."
              : // The AGE is the actionable part. "3 waiting" and "3 waiting,
                // longest 41 minutes" call for different responses.
                `${breaksWaiting} waiting${
                  oldestBreakWaitMin != null ? ` · longest ${oldestBreakWaitMin} min` : ""
                }.`
        }
      >
        {!na && breakWaitingRows.length > 0 && (
          <ul style={s.handoffs}>
            {breakWaitingRows.map((r, i) => (
              <li key={r.alert?.id || `${r.roomName}-${i}`} style={s.handoff}>
                {/* WHO is asking, WHERE, for WHICH break, and HOW LONG. The
                    count alone cannot tell you whether to walk to OB or to
                    Endoscopy, and the wait is what decides who goes first. */}
                <span style={s.hoIn}>{r.providerName || "Someone"}</span>
                {r.roomName && <span style={s.hoRoom}>{r.roomName}</span>}
                {r.breakType && <span style={s.hoSeat}>{String(r.breakType).toUpperCase()}</span>}
                {/* ⚠️ NAMED IS NOT COVERED, AND THE ROW HAS TO SAY WHICH. Both
                    kinds are waiting — that is why they share this queue — but
                    they call for different acts: "nobody asked yet" means find
                    somebody, and a named cover means ring THAT person. Printing
                    only a count would send an AIC hunting for a second reliever
                    for a room that already has one walking to it. The name is
                    the instruction. */}
                {r.kind === "assigned" && (
                  <span
                    style={s.hoNamed}
                    data-testid="band-cover-named"
                    title={`${r.relieverName || "A reliever"} has been asked to cover ${r.roomName || "this room"} and is not in the room yet`}
                  >
                    ⏳ {r.relieverName || "A reliever"} — not in the room yet
                  </span>
                )}
                {/* Null means the request carries no timestamp — say so rather
                    than omitting it, because a missing wait reads as "just now". */}
                <span style={r.waitedMin != null && r.waitedMin >= 20 ? s.hoStale : s.hoWait}>
                  {r.waitedMin != null ? `${r.waitedMin} min` : "age unknown"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Strip>
      {/* A staged handover with a STALE flag is the one that needs somebody:
          either it has sat too long, or — worse — the outgoing person has
          already left, which means the room is being covered by someone the
          board does not believe is in it. So the tone follows the worst row,
          not the count. */}
      <Strip
        title="RELIEF IN PROGRESS"
        count={reliefInProgress}
        tone={reliefRows.some((r) => r?.stale) ? "warn" : "info"}
        stamp={stamp}
        message={
          reliefInProgress === 0
            ? "No relief staged."
            : `${reliefInProgress} handover${reliefInProgress === 1 ? "" : "s"} staged, awaiting confirmation.`
        }
      >
        {reliefRows.length > 0 && (
          <ul style={s.handoffs}>
            {reliefRows.map((r) => (
              <li key={r.caseId || `${r.roomName}-${r.incomingName}`} style={s.handoff}>
                {/* WHO IS RELIEVING WHOM, named. "1 handover staged" tells you
                    something is pending; it does not tell you whether the
                    person you are looking for is the one leaving or the one
                    arriving. */}
                <span style={s.hoOut}>{r.outgoingName || "—"}</span>
                <span style={s.hoArrow} aria-hidden="true">→</span>
                <span style={s.hoIn}>{r.incomingName || "—"}</span>
                {r.roomName && <span style={s.hoRoom}>{r.roomName}</span>}
                {r.seat === "md" && <span style={s.hoSeat}>MD</span>}
                {r.stale && (
                  <span style={s.hoStale}>
                    {r.staleReason === "incoming-left"
                      ? "nobody is coming — relief has left"
                      : r.staleReason === "departed"
                        ? "outgoing already left"
                        : "waiting too long"}
                  </span>
                )}
                {/* ⚠️ CONFIRM IS A REAL TAKEOVER, NOT A DISMISSAL. It writes the
                    incoming person in as the room's provider for the rest of the
                    day and clears the staged slot. So it is refused outright when
                    the person arriving has gone home (`confirmable`) — confirming
                    that hands the room to nobody and then reports it as settled.
                    A row with nobody coming needs a human, not a checkmark. */}
                {onConfirmRelief && (
                  r.confirmable === false ? (
                    <span style={s.hoNoConfirm} title="The incoming provider has gone home — this handover needs re-staging, not confirming.">
                      cannot confirm
                    </span>
                  ) : (
                    <button
                      type="button"
                      style={s.hoConfirm}
                      disabled={busy}
                      onClick={() => onConfirmRelief(r)}
                      // Names the consequence. "Done" or a bare tick would read
                      // as "clear this off my board", and this is not that.
                      title={`Confirm ${r.incomingName || "the relief"} has taken ${r.roomName || "the room"}`}
                      aria-label={`Confirm ${r.incomingName || "the relief"} has taken over ${r.roomName || "the room"} from ${r.outgoingName || "the outgoing provider"}`}
                    >
                      ✓ Confirm
                    </button>
                  )
                )}
              </li>
            ))}
          </ul>
        )}
      </Strip>
    </div>
  );
}

const s = {
  // Auto-fit rather than a fixed four-across: at 585px four strips would be
  // 130px each and the messages would wrap to four lines.
  band: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 10,
    minWidth: 0,
  },
  strip: {
    border: `1px solid ${color.border.default}`,
    borderLeftWidth: 4,
    borderLeftStyle: "solid",
    borderRadius: radius.md,
    padding: "9px 12px 10px",
    minWidth: 0,
    boxShadow: tokens.shadow.sm,
  },
  // Hatched + dashed, so "we cannot tell you" is distinguishable from "zero"
  // without relying on colour.
  stripUnavailable: {
    background: "repeating-linear-gradient(45deg, #cfd8e6 0 4px, #eef2f8 4px 8px)",
    borderStyle: "dashed",
    borderLeftStyle: "dashed",
  },
  head: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 },
  title: {
    margin: 0,
    fontSize: 11,
    fontWeight: font.weight.bold,
    letterSpacing: ".8px",
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    minWidth: 0,
  },
  count: { fontSize: 17, fontWeight: 800, letterSpacing: 0 },
  stamp: { fontSize: 9.5, color: color.text.muted, whiteSpace: "nowrap" },
  message: { margin: "3px 0 0", fontSize: 11.5, lineHeight: 1.4 },
  handoffs: { listStyle: "none", margin: "6px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 3 },
  handoff: { display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", fontSize: 11, lineHeight: 1.35 },
  // Pushed to the RIGHT end of the row (`marginLeft: auto`), where the owner
  // asked for it and where it cannot be hit while reading the room name.
  //
  // Outlined, not filled: on a strip that is already alarm-red, a solid red
  // button would read as a second alarm rather than the answer to the first.
  // 28px tall so it does not grow the strip — these rows are scanned, and a
  // taller emergency line pushes everything below it down mid-alarm.
  clearBtn: {
    marginLeft: "auto",
    flexShrink: 0,
    minHeight: 28,
    padding: "3px 12px",
    fontSize: 11,
    fontWeight: font.weight.bold,
    fontFamily: "inherit",
    color: "#991b1b",
    background: "#ffffff",
    border: "1px solid #991b1b",
    borderRadius: radius.sm,
    cursor: "pointer",
  },
  hoOut: { color: color.text.secondary, textDecoration: "line-through" },
  hoArrow: { color: color.text.muted },
  hoIn: { color: color.text.primary, fontWeight: font.weight.bold },
  hoRoom: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10, color: color.text.muted },
  hoSeat: { fontSize: 9, fontWeight: font.weight.bold, letterSpacing: ".4px", color: color.text.muted, border: `1px solid ${color.border.default}`, borderRadius: radius.full, padding: "0 5px" },
  hoWait: { fontSize: 10, color: color.text.muted, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
  hoStale: { fontSize: 9.5, fontWeight: font.weight.bold, color: "#7a4f00", background: "#fef8ec", border: `1px solid ${color.accent.closing}`, borderRadius: radius.sm, padding: "1px 5px" },
  // ⚠️ THE SAME NAVY THE BREAKS CARD GIVES `assigned`, AND DELIBERATELY SO.
  // That card inverts this one state because "named but NOBODY IN THE ROOM" is
  // what most needs noticing, and it survives a monochrome photo of the wall
  // and a colour-blind reader where the other fills do not. A second colour for
  // the same fact here would teach two vocabularies for one state. White on
  // #1e40af is 8.72:1; the ⏳ and the words carry it with colour stripped.
  hoNamed: {
    fontSize: 9.5,
    fontWeight: font.weight.bold,
    color: "#ffffff",
    background: "#1e40af",
    borderRadius: radius.sm,
    padding: "1px 5px",
  },
  ackLabel: { fontSize: 10, color: color.text.muted },
  // Green, matching "✓ Take over" on the room card — the same act, so it must
  // not wear a different colour or a different verb.
  hoConfirm: {
    fontSize: 9.5,
    fontWeight: 800,
    letterSpacing: ".3px",
    color: "#0a7d5e",
    background: "#e6f8f3",
    border: `1px solid ${color.accent.active}`,
    borderRadius: radius.sm,
    padding: "2px 7px",
    minHeight: 22,
    cursor: "pointer",
  },
  // ⚠️ A REFUSAL HAS TO BE VISIBLE. Rendering nothing here would leave a row
  // that simply has no control, which reads as a board that forgot it — the
  // reader needs to know the checkmark is missing ON PURPOSE and why.
  hoNoConfirm: {
    fontSize: 9.5,
    fontWeight: 700,
    color: color.text.muted,
    border: `1px dashed ${color.border.default}`,
    borderRadius: radius.sm,
    padding: "2px 7px",
  },
  // Louder than `hoStale`: an emergency nobody has picked up is not "waiting
  // too long", it is unanswered. Filled rather than outlined so it reads at a
  // glance, and worded so it survives greyscale.
  hoUnack: {
    fontSize: 9.5,
    fontWeight: 800,
    letterSpacing: ".4px",
    color: "#ffffff",
    background: color.accent.error,
    borderRadius: radius.sm,
    padding: "1px 6px",
  },
};
